import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Boxes, RefreshCw } from "lucide-react";

export default function InwardLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inward-log");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (r.item_name || "").toLowerCase().includes(s) ||
      (r.item_code || "").toLowerCase().includes(s)
    );
  });

  const totals = filtered.reduce(
    (acc, r) => ({
      received: acc.received + Number(r.received || 0),
      issued: acc.issued + Number(r.issued || 0),
      balance: acc.balance + Number(r.balance || 0),
      actual_value: acc.actual_value + Number(r.actual_value || 0),
      gross_value: acc.gross_value + Number(r.gross_value || 0),
    }),
    { received: 0, issued: 0, balance: 0, actual_value: 0, gross_value: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Module · Inventory</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Inward Log</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time stock ledger from GRN inwards and Issue Slip outwards.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="inward-log-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search item or code…"
            className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
          />
          <button
            onClick={load}
            data-testid="inward-log-refresh"
            className="border border-slate-300 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 hover:bg-slate-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-4 py-3 w-12">S.N.</th>
                <th className="text-left px-4 py-3">Item Name</th>
                <th className="text-left px-4 py-3">Item Code</th>
                <th className="text-right px-4 py-3">Opening Stock</th>
                <th className="text-right px-4 py-3">Received Qty</th>
                <th className="text-right px-4 py-3">Issued Qty</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-right px-4 py-3">Actual Value</th>
                <th className="text-right px-4 py-3">Gross Value</th>
              </tr>
            </thead>
            <tbody data-testid="inward-log-list">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-16 text-slate-400">
                    <Boxes size={28} className="mx-auto mb-2 opacity-40" />
                    No inventory yet. Generate a GRN to populate stock.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.sn} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inward-row-${r.item_code || r.item_name}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.sn}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.item_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.item_code || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.opening_stock} {r.unit}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{r.received} {r.unit}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{r.issued} {r.unit}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${r.balance <= 0 ? "text-rose-700" : "text-slate-900"}`}>
                    {r.balance} {r.unit}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">₹{Number(r.actual_value).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{Number(r.gross_value).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200 text-xs uppercase tracking-wider">
                  <td colSpan="4" className="px-4 py-3 text-right font-semibold text-slate-700">Totals</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{totals.received.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{totals.issued.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{totals.balance.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">₹{totals.actual_value.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">₹{totals.gross_value.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
